import Vue from "vue";
import Vuetify from "vuetify";
import "@/assets/mainassets.scss";
import App from "@/App.vue";
import {
  Configuration,
  RedCapitalConfig,
  SelectedCountry,
  Backend,
  ShowDebug
} from "@/configuration";
import router from "@/router";
import store from "@/store";
// @ts-ignore
import VueMeta from "vue-meta";
// @ts-ignore
import VueMultianalytics from "vue-multianalytics";
import VueLazyload from "vue-lazyload";
// @ts-ignore
import VueMoment from "vue-moment";
import DatatableFilterPlugin from "@/filters";
import { themeService } from "@/services/themeService";
import { loadThemeConfig } from "@/helpers/theme/loadThemeConfig";
import { defaultThemeConfig } from "@/config/theme/defaultThemeConfig";
import { themeColorsToVuetify } from "@/helpers/theme/themeColorsToVuetify";
import { loadDynamicScripts } from "@/helpers/theme/loadDynamicScripts";

Vue.config.productionTip = false;

declare module "vue/types/vue" {
  interface Vue {
    $DatatableFilter: any;
  }
}

declare global {
  interface Window {
    grecaptcha: any;
  }
}

let gaConfig = {
  appName: `${Configuration.Analytics.Name}`,
  appVersion: "0.97",
  trackingId: `${Configuration.Analytics.UA}`,
  globalDimensions: [],
  globalMetrics: []
};

Vue.use(VueMultianalytics, {
  modules: {
    ga: gaConfig
  },
  routing: {
    vueRouter: router
  }
});
Vue.use(VueMeta, {
  refreshOnceOnNavigation: true
});
Vue.use(VueLazyload);
Vue.use(DatatableFilterPlugin);
Vue.use(VueMoment);

Vue.use(Vuetify, {
  theme: {
    ...themeColorsToVuetify(defaultThemeConfig.colors),
    white: "#ffffff"
  }
});

Vue.filter("currency", function(value: any) {
  if (value === null || value === undefined) value = 0;
  let strValue = value.toString();
  if (strValue.length == 4) {
    return `${RedCapitalConfig(SelectedCountry).Currency.Prefix} ${
      strValue[0]
    }.${strValue
      .split("")
      .slice(1, 4)
      .join("")}${RedCapitalConfig(SelectedCountry).Currency.Suffix}`;
  } else {
    return `${
      RedCapitalConfig(SelectedCountry).Currency.Prefix
    } ${value.toLocaleString(
      RedCapitalConfig(SelectedCountry).Currency.LocaleString
    )}${RedCapitalConfig(SelectedCountry).Currency.Suffix}`;
  }
});
Vue.filter("ufcurrency", function(value: any) {
  if (value === null || value === undefined) value = 0;
  let strValue = value.toString();
  if (strValue.length == 4) {
    return `${RedCapitalConfig(SelectedCountry).Currency.Uf} ${
      strValue[0]
    }.${strValue
      .split("")
      .slice(1, 4)
      .join("")}${RedCapitalConfig(SelectedCountry).Currency.Suffix}`;
  } else {
    return `${
      RedCapitalConfig(SelectedCountry).Currency.Uf
    } ${value.toLocaleString(
      RedCapitalConfig(SelectedCountry).Currency.LocaleString
    )}${RedCapitalConfig(SelectedCountry).Currency.Suffix}`;
  }
});
Vue.filter("Smallcurrency", function(value: any) {
  if (value === null || value === undefined) value = 0;
  let strValue = value.toString();
  if (strValue.length == 4) {
    return `${RedCapitalConfig(SelectedCountry).Currency.Prefix}${
      strValue[0]
    }.${strValue
      .split("")
      .slice(1, 4)
      .join("")}${RedCapitalConfig(SelectedCountry).Currency.Suffix}`;
  } else {
    return `${value.toLocaleString(
      RedCapitalConfig(SelectedCountry).Currency.LocaleString
    )}${RedCapitalConfig(SelectedCountry).Currency.Suffix}`;
  }
});

Vue.mixin({
  data() {
    return {
      SelectedCountry,
      RedCapital: RedCapitalConfig(SelectedCountry),
      Backend,
      ShowDebug
    };
  }
});

export const EventBus = new Vue();

function hideSplash(): void {
  const splash = document.getElementById("rc-splash");
  if (!splash) return;
  splash.classList.add("rc-splash--hide");
  setTimeout(() => {
    if (splash.parentNode) splash.parentNode.removeChild(splash);
  }, 300);
}

if (!store.getters.isAuthenticated && store.state.user != null) {
  store.commit("clearSession");
}

// Mount diferido hasta que el theme cargue.
// Sin esto, refrescar /registrar (u otras rutas con <g-recaptcha>) falla
// con "Missing required parameters: sitekey" porque el widget se inicializa
// con el default (null) antes de que applyRemote() setee el sitekey remoto.
let mounted = false;
function mountApp(): void {
  if (mounted) return;
  mounted = true;
  new Vue({
    router,
    store,
    render: h => h(App)
  }).$mount("#app");
  hideSplash();
}

// Boot: el theme del tenant debe cargar ANTES de montar la app.
//
//  - /theme responde: aplicar theme del tenant, inyectar scripts dinamicos y
//    montar la app.
//  - /theme falla (null/error): NO montar con el default RedCapital. Mostrar
//    la vista de inconvenientes (#rc-theme-error en index.html) y reintentar
//    con backoff exponencial + jitter (ver scheduleThemeRetry). El boton
//    "Reintentar ahora" dispara un reintento inmediato. Asi un tenant nunca ve
//    la marca RC por una caida de /theme.
//
// loadThemeConfig() nunca rechaza (try/catch interno + timeout axios 3s), asi
// que el primer intento resuelve siempre en <=3s. El .catch queda como red de
// seguridad ante un futuro cambio que pudiera hacerlo rechazar.
// Reintento con backoff exponencial + jitter, capado, y pausado en background.
// Evita saturar /theme cuando esta caido: el intervalo crece tras cada fallo
// (~5s, 10s, 20s, 40s, 60s tope) con +-25% de jitter para que multiples clientes
// no reintenten sincronizados. Si la pestana no esta visible, los REINTENTOS se
// pausan (Page Visibility) y se reanudan al volver al foco. El primer intento y
// el del boton manual corren siempre (1 sola request, no spam).
const THEME_RETRY_BASE_MS = 5000;
const THEME_RETRY_CAP_MS = 60000;
const THEME_RETRY_JITTER = 0.25;
let themeRetryTimer: number | undefined;
let themeRetryAttempt = 0;
let themePendingRetry = false;
let themeFetchInFlight = false;

function nextThemeRetryDelay(): number {
  const exp = Math.min(THEME_RETRY_BASE_MS * Math.pow(2, themeRetryAttempt), THEME_RETRY_CAP_MS);
  const jitter = exp * THEME_RETRY_JITTER * (Math.random() * 2 - 1);
  return Math.round(exp + jitter);
}

function clearThemeRetryTimer(): void {
  if (themeRetryTimer !== undefined) {
    clearTimeout(themeRetryTimer);
    themeRetryTimer = undefined;
  }
}

function showThemeError(): void {
  hideSplash();
  const el = document.getElementById("rc-theme-error");
  if (el) el.style.display = "flex";
}

function hideThemeError(): void {
  const el = document.getElementById("rc-theme-error");
  if (el) el.style.display = "none";
}

function scheduleThemeRetry(): void {
  showThemeError();
  if (themeRetryTimer !== undefined) return; // ya hay un reintento en cola
  if (document.hidden) {
    themePendingRetry = true; // pestana en background: se reintenta al volver al foco
    return;
  }
  const delay = nextThemeRetryDelay();
  themeRetryAttempt++;
  themeRetryTimer = window.setTimeout(() => {
    themeRetryTimer = undefined;
    if (document.hidden) {
      themePendingRetry = true; // dejo de reintentar mientras este en background
      return;
    }
    attemptBoot();
  }, delay);
}

function setRetryBtnBusy(busy: boolean): void {
  const btn = document.getElementById("rc-theme-error-retry") as HTMLButtonElement | null;
  if (!btn) return;
  btn.disabled = busy;
  btn.textContent = busy ? "Reintentando…" : "Reintentar ahora";
}

function attemptBoot(): void {
  // El primer intento (y el del boton manual) corren siempre, aunque la pestana
  // este en background: es 1 sola request. Lo que se pausa en background son los
  // REINTENTOS automaticos (scheduleThemeRetry), la fuente real de carga repetida.
  //
  // Guard in-flight: si ya hay una peticion a /theme en curso, no se lanza otra.
  // Evita que el spam del boton "Reintentar" acumule peticiones concurrentes. Las
  // requests a /theme son identicas e idempotentes, asi que cancelar+relanzar
  // (AbortController) seria redundante — simplemente no duplicamos. El boton se
  // deshabilita mientras hay una en vuelo para dar feedback.
  if (themeFetchInFlight) return;
  themeFetchInFlight = true;
  setRetryBtnBusy(true);
  loadThemeConfig()
    .then(remote => {
      if (remote) {
        clearThemeRetryTimer();
        themeRetryAttempt = 0;
        themePendingRetry = false;
        hideThemeError();
        themeService.applyRemote(remote);
        loadDynamicScripts(themeService.current);
        mountApp();
      } else {
        scheduleThemeRetry();
      }
    })
    .catch(() => {
      scheduleThemeRetry();
    })
    .finally(() => {
      themeFetchInFlight = false;
      setRetryBtnBusy(false);
    });
}

// Al volver la pestana al foco, si quedo un reintento pendiente, hazlo ya.
document.addEventListener("visibilitychange", () => {
  if (!document.hidden && themePendingRetry) {
    themePendingRetry = false;
    attemptBoot();
  }
});

// Boton "Reintentar ahora": reintento inmediato y resetea el backoff.
const retryBtn = document.getElementById("rc-theme-error-retry");
if (retryBtn) {
  retryBtn.addEventListener("click", () => {
    clearThemeRetryTimer();
    themeRetryAttempt = 0;
    themePendingRetry = false;
    attemptBoot();
  });
}

attemptBoot();
